import React, { useEffect } from "react";

import { IoMdArrowDropdown } from "react-icons/io";
import NoDataImg from "../../../../assets/data/nodata.png";
import { scrollToTop, selectedDropdown, sort } from "../../../utils/helpers";
import DropdownComponent from "../../dropdown";
import NumberDropdownComponent from "../../dropdown/number-dropdown";
import TimeDropdownComponent from "../../dropdown/time-dropdown";
import TableRowsLoader from "../loader/table-rows-loader";
import TableRow from "./table-row-investors";

/**
 * InvestorsDatatable Component
 * 
 * Displays paginated datatable of investor records with sortable columns, filterable fields,
 * expandable row details, and highlighted search results. Manages cell rendering, sorting
 * indicators, filter status, and row interactivity.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.fields - Datatable field configurations
 * @param {Array} props.hiddenFieldNames - Fields currently hidden from display
 * @param {Object} props.tableData - Investor records and pagination metadata
 * @param {boolean} props.isLoading - Loading state for data
 * @returns {JSX.Element} Responsive investor datatable with management features
 */
const Datatable = ({ props }) => {
  const { fields, hiddenFieldNames, tableData, isLoading } = props;

  useEffect(() => {
    scrollToTop("table-data");
  }, [tableData]);

  return (
    <div className="shutdowndatadiv">
      <table className="shutdowndatatable" id="table-data" lang="en">
        {isLoading ? (
          <>
            <thead>
              <tr>
                <th>
                  <span style={{ float: "right" }}>No.</span>
                </th>
                <th style={{ color: "transparent" }}>Image</th>
                {fields.map(
                  (field, index) =>
                    !hiddenFieldNames.includes(field.actualName) && (
                      <th key={index} className={field.cName}>
                        <div className="th-container">
                          {field.displayName}
                          <IoMdArrowDropdown className="sort-navbaricon" />
                        </div>
                      </th>
                    )
                )}
              </tr>
            </thead>
            <TableRowsLoader rowsNum={18} colsNum={fields.length} />
          </>
        ) : tableData.results.length === 0 ? (
          <div className="emptytable">
            {/* <p>No results found</p> */}
            <img src={NoDataImg} alt="no results found" className="nodataimg" />
            <span className="nodatatitle">No results found</span>
            <p className="nodataexp">
              Try adjusting your search or filter to find what you are looking
              for.
            </p>
          </div>
        ) : (
          <>
            <thead>
              <tr>
                <th>
                  <span style={{ float: "right" }}>No.</span>
                </th>
                <th style={{ color: "transparent" }}>Image</th>
                {fields.map((field, index) => (
                  <>
                    {field.actualName === "name" &&
                      !hiddenFieldNames.includes("name") && (
                        <th className="">
                          <div className="th-container">
                            Name
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "investorType" &&
                      !hiddenFieldNames.includes("investorType") && (
                        <th className="sticky z-1">
                          <div className="th-container">
                            Investor type
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "description" &&
                      !hiddenFieldNames.includes("description") && (
                        <th className="">
                          <div className="th-container">
                            Description
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "numOfFailedInvestments" &&
                      !hiddenFieldNames.includes("numOfFailedInvestments") && (
                        <th className="">
                          <div className="th-container">
                            No. of failed investments
                            <span style={{ float: "right" }}>
                              <NumberDropdownComponent
                                props={props}
                                field={field}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "lossOnInvestments" &&
                      !hiddenFieldNames.includes("lossOnInvestments") && (
                        <th className="">
                          <div className="th-container">
                            Loss on investments
                            <span style={{ float: "right" }}>
                              <NumberDropdownComponent
                                props={props}
                                field={field}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "shutteredPortfolioCompanies" &&
                      !hiddenFieldNames.includes(
                        "shutteredPortfolioCompanies"
                      ) && (
                        <th className="">
                          <div className="th-container">
                            Shuttered portfolio companies
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "numOfExit" &&
                      !hiddenFieldNames.includes("numOfExit") && (
                        <th className="">
                          <div className="th-container">
                            No. of exits
                            <span style={{ float: "right" }}>
                              <NumberDropdownComponent
                                props={props}
                                field={field}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "exitValue" &&
                      !hiddenFieldNames.includes("exitValue") && (
                        <th className="">
                          <div className="th-container">
                            Total exit value
                            <span style={{ float: "right" }}>
                              <NumberDropdownComponent
                                props={props}
                                field={field}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "reputation" &&
                      !hiddenFieldNames.includes("reputation") && (
                        <th className="">
                          <div className="th-container">
                            Investor reputation
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "location" &&
                      !hiddenFieldNames.includes("location") && (
                        <th className="">
                          <div className="th-container">
                            Location
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "investmentStage" &&
                      !hiddenFieldNames.includes("investmentStage") && (
                        <th className="">
                          <div className="th-container">
                            Preferred investment stage(s)
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "investmentTrackRecord" &&
                      !hiddenFieldNames.includes("investmentTrackRecord") && (
                        <th className="">
                          <div className="th-container">
                            Stages invested in
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "assets" &&
                      !hiddenFieldNames.includes("assets") && (
                        <th className="">
                          <div className="th-container">
                            Assets under management (AUM)
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "investmentRange" &&
                      !hiddenFieldNames.includes("investmentRange") && (
                        <th className="">
                          <div className="th-container">
                            Investment range
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "sweetSpot" &&
                      !hiddenFieldNames.includes("sweetSpot") && (
                        <th className="">
                          <div className="th-container">
                            Investment sweet spot
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "investmentFocus" &&
                      !hiddenFieldNames.includes("investmentFocus") && (
                        <th className="">
                          <div className="th-container">
                            Preferred investment focus
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "investmentGeography" &&
                      !hiddenFieldNames.includes("investmentGeography") && (
                        <th className="">
                          <div className="th-container">
                            Preferred investment geography
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "numOfInvestments" &&
                      !hiddenFieldNames.includes("numOfInvestments") && (
                        <th className="">
                          <div className="th-container">
                            No. of investments
                            <span style={{ float: "right" }}>
                              <NumberDropdownComponent
                                props={props}
                                field={field}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "numOfLeadInvestments" &&
                      !hiddenFieldNames.includes("numOfLeadInvestments") && (
                        <th className="">
                          <div className="th-container">
                            No. of lead investments
                            <span style={{ float: "right" }}>
                              <NumberDropdownComponent
                                props={props}
                                field={field}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "numOfFollowOnInvestments" &&
                      !hiddenFieldNames.includes(
                        "numOfFollowOnInvestments"
                      ) && (
                        <th className="">
                          <div className="th-container">
                            No. of follow-on investments
                            <span style={{ float: "right" }}>
                              <NumberDropdownComponent
                                props={props}
                                field={field}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "numOfCoInvestments" &&
                      !hiddenFieldNames.includes("numOfCoInvestments") && (
                        <th className="">
                          <div className="th-container">
                            No. of co-investments
                            <span style={{ float: "right" }}>
                              <NumberDropdownComponent
                                props={props}
                                field={field}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "portfolioCompanies" &&
                      !hiddenFieldNames.includes("portfolioCompanies") && (
                        <th>
                          Portfolio companies
                          <span style={{ float: "right" }}>
                            <DropdownComponent
                              props={props}
                              field={field}
                              primary={field.actualName === "name"}
                            />
                          </span>
                        </th>
                      )}
                    {field.actualName === "numOfAcquisitions" &&
                      !hiddenFieldNames.includes("numOfAcquisitions") && (
                        <th className="">
                          <div className="th-container">
                            No. of acquisitions
                            <span style={{ float: "right" }}>
                              <NumberDropdownComponent
                                props={props}
                                field={field}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "boardSeats" &&
                      !hiddenFieldNames.includes("boardSeats") && (
                        <th className="">
                          <div className="th-container">
                            No. of board seats taken
                            <span style={{ float: "right" }}>
                              <NumberDropdownComponent
                                props={props}
                                field={field}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "numOfFunds" &&
                      !hiddenFieldNames.includes("numOfFunds") && (
                        <th className="">
                          <div className="th-container">
                            No. of funds
                            <span style={{ float: "right" }}>
                              <NumberDropdownComponent
                                props={props}
                                field={field}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "limitedPartners" &&
                      !hiddenFieldNames.includes("limitedPartners") && (
                        <th className="">
                          <div className="th-container">
                            Limited partners
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "coInvestors" &&
                      !hiddenFieldNames.includes("coInvestors") && (
                        <th className="">
                          <div className="th-container">
                            Co-investors
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "numofPartners" &&
                      !hiddenFieldNames.includes("numofPartners") && (
                        <th className="">
                          <div className="th-container">
                            No. of partners
                            <span style={{ float: "right" }}>
                              <NumberDropdownComponent
                                props={props}
                                field={field}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "partners" &&
                      !hiddenFieldNames.includes("partners") && (
                        <th className="">
                          <div className="th-container">
                            Partners
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "numOfEmployeesObj" &&
                      !hiddenFieldNames.includes("numOfEmployeesObj") && (
                        <th className="">
                          <div className="th-container">
                            No. of employees
                            <span style={{ float: "right" }}>
                              <NumberDropdownComponent
                                props={props}
                                field={field}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "management" &&
                      !hiddenFieldNames.includes("management") && (
                        <th className="">
                          <div className="th-container">
                            Management
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "boardMembers" &&
                      !hiddenFieldNames.includes("boardMembers") && (
                        <th className="">
                          <div className="th-container">
                            Board members
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "founders" &&
                      !hiddenFieldNames.includes("founders") && (
                        <th className="">
                          <div className="th-container">
                            Founders
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "email" &&
                      !hiddenFieldNames.includes("email") && (
                        <th className="">
                          <div className="th-container">
                            Email
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "socialMedia" &&
                      !hiddenFieldNames.includes("socialMedia") && (
                        <th className="">
                          <div className="th-container">
                            Social media
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "website" &&
                      !hiddenFieldNames.includes("website") && (
                        <th className="">
                          <div className="th-container">
                            Website
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "phone" &&
                      !hiddenFieldNames.includes("phone") && (
                        <th className="">
                          <div className="th-container">
                            Phone no.
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "address" &&
                      !hiddenFieldNames.includes("address") && (
                        <th className="">
                          <div className="th-container">
                            Address
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "entityType" &&
                      !hiddenFieldNames.includes("entityType") && (
                        <th className="">
                          <div className="th-container">
                            Entity type
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "legalName" &&
                      !hiddenFieldNames.includes("legalName") && (
                        <th className="">
                          <div className="th-container">
                            Legal name
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "primaryBusinessName" &&
                      !hiddenFieldNames.includes("primaryBusinessName") && (
                        <th className="">
                          <div className="th-container">
                            Other business name(s)
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "formerlegalName" &&
                      !hiddenFieldNames.includes("formerlegalName") && (
                        <th className="">
                          <div className="th-container">
                            Former legal name(s)
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "founded" &&
                      !hiddenFieldNames.includes("founded") && (
                        <th className="">
                          <div className="th-container">
                            Founded date
                            <span style={{ float: "right" }}>
                              <TimeDropdownComponent
                                onClick={(value, header) =>
                                  selectedDropdown(
                                    props,
                                    value,
                                    field.actualName
                                  )
                                }
                              />
                            </span>
                          </div>
                        </th>
                      )}
                    {field.actualName === "stockSymbol" &&
                      !hiddenFieldNames.includes("stockSymbol") && (
                        <th className="">
                          <div className="th-container">
                            Ticker
                            <span style={{ float: "right" }}>
                              <DropdownComponent
                                props={props}
                                field={field}
                                primary={field.actualName === "name"}
                              />
                            </span>
                          </div>
                        </th>
                      )}
                  </>
                ))}
              </tr>
            </thead>
            <tbody>
              <TableRow props2={props} />
            </tbody>
          </>
        )}
      </table>
    </div>
  );
};

export default Datatable;
